<template>
  <div class="upgrade-rules-management-page">
    <hr class="separator " style="margin-top: 1px"/>

    <div>
      <div class="custom-text">
        <span class="user-manage">{{ $t('UpgradeRulesManagement.title') }}</span>
        
      </div>
      <hr class="separator"/>

    </div>
    <div class="content-container">
      <!-- 搜索功能组件 -->
      <SearchBar @search="handleSearch"/>

      <!-- 定制账号表格 -->
      <DataTable :data="tableData" :loading="loading" @refresh="loadData"/>

      <!-- 分页组件 -->
      <div class="pagination">
        <PaginationBar :totalRecords="totalRecords" :current-page="currentPage" @page-change="handlePageChange"
                       @page-size-change="handlePageSizeChange" :page-size="pageSize"/>
      </div>
    </div>
  </div>
</template>

<script>
import SearchBar from './components/SearchBar.vue';
import DataTable from './components/DataTable.vue';
import PaginationBar from '@/components/PaginationBar.vue';
import {getUpgradeRulesList, getUserPermissions} from "@/api/api";

export default {
  components: {
    SearchBar,
    DataTable,
    PaginationBar,
  },
  data() {
    return {
      tableData: [], // 升级规则数据
      totalRecords: 0, // 总记录数
      currentPage: 1, // 当前页码
      pageSize: 10,
      isSearching: false,
      searchParams: {},
      loading: false, // 加载状态
      userPermissions: [], // 用户权限
    };
  },
  
  computed: {
    // 其他计算属性可以在这里添加
  },

  created() {
    this.loadData();
  },
  
  mounted() {
    this.loadUserPermissions();
  },
  watch: {
    // 监听语言切换事件
    '$i18n.locale': function () {
      // 语言切换后重新加载表格数据
      if (this.isSearching) {
        this.handleSearch(this.searchParams);
      } else {
        this.loadData();
      }
    },
  },
  methods: {
    // 加载用户权限
    async loadUserPermissions() {
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const cacheKey = `userPermissions_${userId}`;
        
        // 首先尝试从localStorage获取
        const cache = localStorage.getItem(cacheKey) || sessionStorage.getItem(cacheKey);
        if (cache) {
          this.userPermissions = JSON.parse(cache);
        } else {
          // 如果本地没有缓存，则调用API获取权限
          const rawPermissions = await getUserPermissions();
          localStorage.setItem(cacheKey, JSON.stringify(rawPermissions.menus));
          this.userPermissions = rawPermissions.menus;
        }
      } catch (error) {
        console.error('Error accessing storage:', error);
        this.userPermissions = [];
      }
    },
    
    // 检查权限
    hasPermission(permission) {
      if (!this.userPermissions || !Array.isArray(this.userPermissions)) {
        return false;
      }
      
      const hasPermissionRecursive = (permissions) => {
        return permissions.some(perm => {
          if (perm.code === permission) {
            return true;
          }
          if (perm.children && Array.isArray(perm.children)) {
            return hasPermissionRecursive(perm.children);
          }
          return false;
        });
      };
      
      return hasPermissionRecursive(this.userPermissions);
    },
    
    async handleSearch(searchParams) {
      const page = this.currentPage;
      const pageSize = this.pageSize;

      this.isSearching = true;
      this.searchParams = searchParams;
      this.loading = true; // 开始加载
      try {
        const response = await getUpgradeRulesList(page, pageSize, this.searchParams);
        // 在这里进行数据转换 - 适配新的数据库字段
        this.tableData = response.data.map(item => ({
          id: item.id,
          type: item.type,
          userId: item.user_id,
          version: item.version,
          createdBy: item.created_by,
          enable: item.enable,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          typeText: this.getTypeText(item.type),
          enableText: this.getEnableText(item.enable),
          createdUser: item.createdUser, // 添加创建人信息
          user: item.user // 添加用户信息，用于显示用户邮箱
        }));

        this.totalRecords = response.meta.total;
        this.currentPage = response.meta.current_page;
        this.pageSize = parseInt(response.meta.per_page);

      } catch (error) {
        console.error(this.$t('ProductManage.searchFailed'));
      } finally {
        this.loading = false; // 加载结束
      }
    },

    async handlePageChange(newPage) {
      // 异步操作的处理逻辑
      try {
        this.currentPage = newPage;
        // 根据新的页码发送请求获取数据，更新tableData
        if (this.isSearching) {
          await this.handleSearch(this.searchParams);
        } else {
          await this.loadData();
        }
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async handlePageSizeChange(newPageSize) {
      // 异步操作的处理逻辑
      try {
        this.pageSize = newPageSize;
        // 根据新的页码发送请求获取数据，更新tableData
        await this.handleSearch(this.searchParams);
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async loadData() {
      this.isSearching = false;
      const page = this.currentPage;
      const pageSize = this.pageSize;
      this.loading = true; // 开始加载
      try {
        const data = await getUpgradeRulesList(page, pageSize);
        // 将从服务器获取的数据进行字段重命名 - 适配新的数据库字段
        this.tableData = data.data.map(item => ({
          id: item.id,
          type: item.type,
          userId: item.user_id,
          version: item.version,
          createdBy: item.created_by,
          enable: item.enable,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          typeText: this.getTypeText(item.type),
          enableText: this.getEnableText(item.enable),
          createdUser: item.createdUser, // 添加创建人信息
          user: item.user // 添加用户信息，用于显示用户邮箱
        }));

        this.totalRecords = data.meta.total;
        this.currentPage = data.meta.current_page;
        this.pageSize = typeof data.meta.per_page === 'string' ? parseInt(data.meta.per_page, 10) : data.meta.per_page;
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      } finally {
        this.loading = false; // 加载结束
      }
    },

    // 获取类型文本
    getTypeText(type) {
      const typeMap = {
        0: this.$t('UpgradeRulesManagement.typeFirmware'),
        1: this.$t('UpgradeRulesManagement.typeParameter')
      };
      return typeMap[type] || type;
    },

    // 获取启用状态文本
    getEnableText(enable) {
      return enable ? this.$t('UpgradeRulesManagement.enable') : this.$t('UpgradeRulesManagement.disable');
    }
  },
};
</script>
<style scoped>
@import "@/assets/css/publicStyle.css";

.upgrade-rules-management-page {
  width: 100%;
  min-height: 0;
  height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.content-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

/* 分页组件样式 */
.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  margin-bottom: 20px;
}
</style>
